/**
 * API call tracking and rate limiting via Firebase Realtime Database.
 *
 * Tracks daily API call totals, per-IP usage over a rolling 30-day window, and
 * daily unique users. Enforces a 50-call limit per IP per 30 days for free-tier
 * users (premium users are exempt). Also handles periodic cleanup of stale data
 * and a one-time migration from the old daily-users format.
 */
import { ref, get, serverTimestamp, runTransaction } from "firebase/database";
import { database } from "./firebase";

/**
 * Get user's IP address (using a free IP detection service)
 */
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP address:', error);
    // Fallback to a placeholder if IP detection fails
    return 'unknown';
  }
};

/**
 * Sanitize IP address for use as Firebase path
 * Replace dots with underscores since dots are invalid in Firebase paths
 */
const sanitizeIPAddress = (ipAddress) => {
  return ipAddress.replace(/\./g, '_');
};

/**
 * Get current date in YYYY-MM-DD format
 */
const getCurrentDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

/**
 * Get date 30 days ago in YYYY-MM-DD format
 */
const getDate30DaysAgo = () => {
  const now = new Date();
  now.setDate(now.getDate() - 30);
  return now.toISOString().split('T')[0];
};

/**
 * Increment daily API call counter
 */
const incrementDailyCounter = async () => {
  try {
    const today = getCurrentDate();
    const dailyCounterRef = ref(database, `api-calls/daily/${today}`);
    
    await runTransaction(dailyCounterRef, (currentValue) => {
      return (currentValue || 0) + 1;
    });
  } catch (error) {
    console.error('Error incrementing daily counter:', error);
  }
};

/**
 * Track unique users per day
 */
const trackDailyUniqueUser = async (ipAddress) => {
  try {
    const today = getCurrentDate();
    const sanitizedIP = sanitizeIPAddress(ipAddress);
    
    // First check if this IP has already been counted today
    const ipRef = ref(database, `api-calls/daily-users-ips/${today}/${sanitizedIP}`);
    const ipSnapshot = await get(ipRef);
    
    if (!ipSnapshot.exists()) {
      // This is a new unique user for today
      // Mark the IP as seen
      await runTransaction(ipRef, () => true);
      
      // Increment the daily unique users count
      const dailyCountRef = ref(database, `api-calls/daily-users/${today}`);
      await runTransaction(dailyCountRef, (currentValue) => {
        return (currentValue || 0) + 1;
      });
    }
    
    // Occasionally clean up old daily-users data (run cleanup 5% of the time)
    if (Math.random() < 0.05) {
      cleanupOldDailyUsers();
      cleanupOldDailyUsersIPs();
    }
  } catch (error) {
    console.error('Error tracking daily unique user:', error);
  }
};

/**
 * Clean up old daily-users data (keep only last 30 days)
 */
const cleanupOldDailyUsers = async () => {
  try {
    const cutoffDate = getDate30DaysAgo();
    const dailyUsersRef = ref(database, 'api-calls/daily-users');
    
    await runTransaction(dailyUsersRef, (currentData) => {
      if (!currentData) return currentData;
      
      const cleanedData = {};
      Object.keys(currentData).forEach(date => {
        if (date >= cutoffDate) {
          cleanedData[date] = currentData[date];
        }
      });
      
      return cleanedData;
    });
    
    console.log('Old daily-users data cleaned up');
  } catch (error) {
    console.error('Error cleaning up old daily-users data:', error);
  }
};

/**
 * Clean up old daily-users-ips data (keep only last 30 days)
 */
const cleanupOldDailyUsersIPs = async () => {
  try {
    const cutoffDate = getDate30DaysAgo();
    const dailyUsersIPsRef = ref(database, 'api-calls/daily-users-ips');
    
    await runTransaction(dailyUsersIPsRef, (currentData) => {
      if (!currentData) return currentData;
      
      const cleanedData = {};
      Object.keys(currentData).forEach(date => {
        if (date >= cutoffDate) {
          cleanedData[date] = currentData[date];
        }
      });
      
      return cleanedData;
    });
    
    console.log('Old daily-users-ips data cleaned up');
  } catch (error) {
    console.error('Error cleaning up old daily-users-ips data:', error);
  }
};

/**
 * Update IP address tracking
 */
const updateIPTracking = async (ipAddress) => {
  try {
    const today = getCurrentDate();
    const sanitizedIP = sanitizeIPAddress(ipAddress);
    const ipRef = ref(database, `api-calls/by-ip/${sanitizedIP}`);
    
    await runTransaction(ipRef, (currentData) => {
      const data = currentData || {
        dailyCount: {},
        last30Days: 0,
        lastUpdated: null
      };
      
      // Increment today's count for this IP
      data.dailyCount[today] = (data.dailyCount[today] || 0) + 1;
      
      // Clean up old daily counts (keep only last 30 days)
      const cutoffDate = getDate30DaysAgo();
      Object.keys(data.dailyCount).forEach(date => {
        if (date < cutoffDate) {
          delete data.dailyCount[date];
        }
      });
      
      // Calculate 30-day total
      data.last30Days = Object.values(data.dailyCount).reduce((sum, count) => sum + count, 0);
      data.lastUpdated = serverTimestamp();
      
      return data;
    });
  } catch (error) {
    console.error('Error updating IP tracking:', error);
  }
};

/**
 * Track API call - increments daily total, IP-specific counters, and unique daily users
 */
export const trackAPICall = async () => {
  try {
    // Get user's IP address
    const ipAddress = await getUserIP();
    
    // Run all tracking operations in parallel
    await Promise.all([
      incrementDailyCounter(),
      updateIPTracking(ipAddress),
      trackDailyUniqueUser(ipAddress)
    ]);
    
    console.log('API call tracked successfully');
  } catch (error) {
    console.error('Error tracking API call:', error);
    // Don't throw error to avoid breaking the main app functionality
  }
};

/**
 * Check if user has exceeded the 50 API call limit in the last 30 days
 * Premium users are exempt from rate limits
 */
export const hasExceededRateLimit = async (isPremium = false) => {
  try {
    // Premium users are exempt from rate limits
    if (isPremium) {
      return { exceeded: false, callCount: 0, nextAvailableDate: null };
    }
    
    const ipAddress = await getUserIP();
    const sanitizedIP = sanitizeIPAddress(ipAddress);
    const ipRef = ref(database, `api-calls/by-ip/${sanitizedIP}`);
    
    const snapshot = await get(ipRef);
    const data = snapshot.val();
    
    if (!data) {
      return { exceeded: false, callCount: 0, nextAvailableDate: null };
    }
    
    // Clean up old daily counts and recalculate 30-day total
    const cutoffDate = getDate30DaysAgo();
    const validDailyCounts = {};
    
    Object.keys(data.dailyCount || {}).forEach(date => {
      if (date >= cutoffDate) {
        validDailyCounts[date] = data.dailyCount[date];
      }
    });
    
    const callCount = Object.values(validDailyCounts).reduce((sum, count) => sum + count, 0);
    const exceeded = callCount >= 50;
    
    // Calculate when user will have more calls available (when oldest call falls off)
    let nextAvailableDate = null;
    if (exceeded) {
      // Find the oldest date with calls
      const dates = Object.keys(validDailyCounts).sort();
      if (dates.length > 0) {
        const oldestDate = new Date(dates[0]);
        const nextDate = new Date(oldestDate);
        nextDate.setDate(nextDate.getDate() + 31); // 31 days after oldest call
        nextAvailableDate = nextDate.toISOString().split('T')[0];
      }
    }
    
    return { exceeded, callCount, nextAvailableDate };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // On error, allow the API call to proceed
    return { exceeded: false, callCount: 0, nextAvailableDate: null };
  }
};

/**
 * Migrate old daily-users data format to new format (one-time migration)
 * Converts from {date: {ip1: true, ip2: true}} to {date: count}
 */
export const migrateDailyUsersData = async () => {
  try {
    console.log('Starting daily-users data migration...');
    const dailyUsersRef = ref(database, 'api-calls/daily-users');
    
    await runTransaction(dailyUsersRef, (currentData) => {
      if (!currentData) return currentData;
      
      const migratedData = {};
      Object.keys(currentData).forEach(date => {
        const dateData = currentData[date];
        // Check if this date has the old format (object with IPs as keys)
        if (typeof dateData === 'object' && dateData !== null) {
          // Convert to count
          migratedData[date] = Object.keys(dateData).length;
        } else {
          // Already in new format (number)
          migratedData[date] = dateData;
        }
      });
      
      return migratedData;
    });
    
    console.log('Daily-users data migration completed');
    return true;
  } catch (error) {
    console.error('Error migrating daily-users data:', error);
    return false;
  }
};

/**
 * Test the new unique user tracking (for debugging)
 */
export const testUniqueUserTracking = async () => {
  try {
    console.log('Testing unique user tracking...');
    await trackAPICall();
    
    // Get today's stats
    const stats = await getAPIStats();
    console.log('Today\'s API stats:', {
      totalCalls: stats.todayTotal,
      uniqueUsers: stats.todayUniqueUsers
    });
    
    return stats;
  } catch (error) {
    console.error('Error testing unique user tracking:', error);
    return null;
  }
};

/**
 * Get API usage statistics (for debugging/monitoring)
 */
export const getAPIStats = async () => {
  try {
    const today = getCurrentDate();
    
    // Get today's total calls
    const dailyRef = ref(database, `api-calls/daily/${today}`);
    const dailySnapshot = await get(dailyRef);
    const todayTotal = dailySnapshot.val() || 0;
    
    // Get today's unique users (now stored as a count)
    const dailyUsersRef = ref(database, `api-calls/daily-users/${today}`);
    const dailyUsersSnapshot = await get(dailyUsersRef);
    const todayUniqueUsers = dailyUsersSnapshot.val() || 0;
    
    // Get last 7 days totals and unique users
    const last7Days = {};
    const last7DaysUsers = {};
    
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Get total calls for this day
      const dayRef = ref(database, `api-calls/daily/${dateStr}`);
      const daySnapshot = await get(dayRef);
      last7Days[dateStr] = daySnapshot.val() || 0;
      
      // Get unique users for this day (now stored as a count)
      const dayUsersRef = ref(database, `api-calls/daily-users/${dateStr}`);
      const dayUsersSnapshot = await get(dayUsersRef);
      last7DaysUsers[dateStr] = dayUsersSnapshot.val() || 0;
    }
    
    return {
      todayTotal,
      todayUniqueUsers,
      last7Days,
      last7DaysUsers
    };
  } catch (error) {
    console.error('Error getting API stats:', error);
    return null;
  }
};